import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";

/**
 * Floating "Ask Now" button that appears when user selects text
 * inside a specified container (question or answer areas).
 *
 * Props:
 *  - containerRef : React ref to the DOM element to listen on
 *  - onAskNow     : (selectedText: string) => void
 */
export default function AskNowPopup({ containerRef, onAskNow }) {
  const [popup, setPopup] = useState({ visible: false, x: 0, y: 0, text: "" });
  const popupRef = useRef(null);
  const debounceRef = useRef(null);

  /* ---- show popup after selection ---- */
  const handleSelectionChange = useCallback(() => {
    // debounce to avoid flicker while dragging
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();

      if (!text || text.length < 3) {
        setPopup((p) => ({ ...p, visible: false }));
        return;
      }

      // Make sure the selection is inside our container
      if (
        containerRef.current &&
        sel.rangeCount > 0
      ) {
        const range = sel.getRangeAt(0);
        const ancestor = range.commonAncestorContainer;
        const isInside =
          containerRef.current.contains(ancestor) ||
          containerRef.current === ancestor;

        if (!isInside) {
          setPopup((p) => ({ ...p, visible: false }));
          return;
        }

        const rect = range.getBoundingClientRect();
        // Position the popup above the selection, centered horizontally
        setPopup({
          visible: true,
          x: rect.left + rect.width / 2,
          y: rect.top - 6, // 6px above selection
          text,
        });
      }
    }, 200);
  }, [containerRef]);

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      clearTimeout(debounceRef.current);
    };
  }, [handleSelectionChange]);

  /* ---- hide when clicking outside the popup ---- */
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        // Small delay so the click on "Ask Now" can fire first
        setTimeout(() => setPopup((p) => ({ ...p, visible: false })), 150);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  /* ---- handle Ask Now click ---- */
  const handleClick = () => {
    if (popup.text) {
      onAskNow(popup.text);
      window.getSelection()?.removeAllRanges();
      setPopup({ visible: false, x: 0, y: 0, text: "" });
    }
  };

  if (!popup.visible) return null;

  return createPortal(
    <div
      ref={popupRef}
      className="ask-now-popup"
      style={{
        position: "fixed",
        top: `${popup.y}px`,
        left: `${popup.x}px`,
        transform: "translate(-50%, -100%)",
        zIndex: 10001,
      }}
    >
      <button className="ask-now-btn" onClick={handleClick}>
        <Search size={14} />
        <span>Ask Now</span>
      </button>
      {/* little triangle pointing down */}
      <div className="ask-now-arrow" />
    </div>,
    document.body
  );
}
